import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button, Card } from './ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="fixed inset-0 flex items-center justify-center p-6"
          style={{
            background: "var(--bg-primary)",
            opacity: 0,
            animation: 'errorFadeIn 200ms ease forwards',
          }}
        >
          <Card
            variant="glass"
            padding="none"
            className="relative z-10 w-full max-w-md shadow-2xl shadow-black/10 dark:shadow-lime-500/5"
          >
            <div className="p-8 flex flex-col items-center text-center">
              {/* Alert icon */}
              <div
                className="mb-5 w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(244, 63, 94, 0.1)",
                  border: "1px solid rgba(244, 63, 94, 0.15)",
                }}
              >
                <AlertTriangle size={28} className="text-rose-500" />
              </div>

              {/* Title */}
              <h2
                className="text-xl font-semibold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Oops, algo sali&oacute; mal
              </h2>

              {/* Message */}
              <p
                className="text-sm mb-5"
                style={{ color: "var(--text-secondary)" }}
              >
                No te preocupes, esto es temporal.
              </p>

              {/* Error details */}
              {this.state.error?.message && (
                <div
                  className="w-full mb-6 rounded-xl p-3 text-left overflow-auto max-h-32"
                  style={{
                    background: "var(--bg-hover)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <code
                    className="text-xs font-mono break-all leading-relaxed"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {this.state.error.message}
                  </code>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3 w-full">
                <Button
                  variant="primary"
                  size="lg"
                  icon={<RefreshCw size={16} />}
                  onClick={this.handleRetry}
                  className="w-full"
                >
                  Intentar de nuevo
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  icon={<Home size={16} />}
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Volver al inicio
                </Button>
              </div>
            </div>
          </Card>

          <style>{`
            @keyframes errorFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export function SafeRender({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <ErrorBoundary fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
